/**
 * The verbs. Everything a crown can *do* to another crown that is not a march.
 *
 * Both doors open onto this file: `commands.ts` calls it for the player and
 * `diploturn.ts` calls it for the council, and they call the same functions with
 * the same arguments. That is the rule the campaign already keeps for musters —
 * an AI that can cheat its own economy is weather rather than an opponent — and
 * it matters more here, because a diplomacy the player can use and the AI cannot
 * is a menu.
 *
 * Every acceptance test below is deterministic and stated as a threshold rather
 * than a roll. A player who can read "they will ally with you at +20" can *plan*
 * a relationship; a player facing a coin flip can only retry. The rolls in this
 * system are all in `diploturn.ts`, and they decide whether the AI *asks*, never
 * whether it says yes.
 */

import { confessionDef } from '../../data/confessions.ts';
import {
  addOffer,
  addOpinion,
  addOpinionBoth,
  addOpinionRealm,
  addTreaty,
  alliesOf,
  coalitionStrength,
  dropAllTreaties,
  freeSubject,
  hasMarriage,
  hasOfferFrom,
  isAllied,
  liegeOf,
  nationStrength,
  offersFor,
  opinion,
  opinionTerms,
  provincesHeld,
  removeOffer,
  removeTreaty,
  sameRealm,
  setSubject,
  subjectsOf,
  truceTurnsLeft,
} from './diplomacy.ts';
import {
  addPassage,
  graceLeft,
  hasPassage,
  isIntruding,
  isTrespassing,
  passageGrantedBy,
  passageHeldBy,
  removePassage,
} from './diplopass.ts';
import {
  ANNEX_OPINION,
  ANNEX_TURNS,
  MAX_ALLIES,
  OFFER_TURNS,
  OfferKind,
  OP_ABANDONED_REALM,
  OP_GRANTED_PASSAGE,
  OP_REFUSED_PASSAGE,
  OP_REVOKED_PASSAGE,
  OP_REVOKED_REALM,
  OP_TOOK_LAND_REALM,
  PASSAGE_FEAR_OPINION,
  PASSAGE_FEAR_RATIO,
  PASSAGE_OPINION,
  SubjectKind,
  TreatyKind,
  UNION_INTEGRATE_TURNS,
  type DiploOffer,
} from './diplotypes.ts';
import {
  availableTerms,
  breakAlliancePenalty,
  casusBelliAgainst,
  declareWarReason,
  joinWar,
  makePeace,
  refuseCall,
  scoreFor,
  sideOf,
  warById,
  warsOf,
  type PeaceOption,
} from './war.ts';
import { checkElimination, logLine, nationName, provinceName } from './state.ts';
import type { CampaignState } from './types.ts';

/** What the world map's diplomacy buttons map onto. `Cmd.CampaignDiplomacy`'s `a`. */
export const DiploAction = {
  ProposeAlliance: 0,
  BreakAlliance: 1,
  ProposeMarriage: 2,
  /** Ask them to bend the knee without a war. Only the very weak ever do. */
  DemandVassalage: 3,
  /** Swallow a subject you have held long enough. */
  AnnexSubject: 4,
  /** Let one go. Costs nothing, and they remember it. */
  ReleaseSubject: 5,
  /**
   * Ask for the run of their ground. The three passage verbs ride this door
   * rather than taking commands of their own, and that was a decision.
   *
   * `Cmd` 121–126 were reserved for this feature in `COORDINATION.md` and all
   * six are released unused. Every other standing arrangement in the game —
   * alliance, marriage, vassalage, annexation, release — is `(action, target)`
   * behind `Cmd.CampaignDiplomacy`, and passage is exactly that shape. A fifth
   * command range would have bought a different integer for an identical call
   * and cost `commands.ts` a new branch in `isCampaignCommand`, which is a file
   * three other branches are also touching this week.
   */
  RequestPassage: 6,
  /** Open your border to them, unasked. Costs nothing and is worth remembering. */
  GrantPassage: 7,
  /** Shut it again. The most aggressive thing here that is not a declaration. */
  RevokePassage: 8,
} as const;
export type DiploActionId = (typeof DiploAction)[keyof typeof DiploAction];

// ── Alliances ──────────────────────────────────────────────────────────────

/** Why these two could not ally, in words, or the empty string if they could. */
export function allianceReason(c: CampaignState, a: number, b: number): string {
  if (a === b || a < 0 || b < 0) return 'There is nobody there.';
  if (!c.nations[a]?.alive || !c.nations[b]?.alive) return 'They no longer hold anything.';
  if (isAllied(c, a, b)) return 'You are already allied.';
  if (c.nations[a].atWar[b] === 1) return 'You are at war with them.';
  if (liegeOf(c, a) >= 0 || liegeOf(c, b) >= 0) return 'A sworn crown does not make its own friends.';
  if (alliesOf(c, a).length >= MAX_ALLIES) return 'You have as many allies as you can keep.';
  if (alliesOf(c, b).length >= MAX_ALLIES) return `${nationName(c, b)} has no room for another ally.`;
  return '';
}

/**
 * The AI's answer, and it is a number the panel can print.
 *
 * The two are set relative to the *scale opinion actually reaches*, which is a
 * thing that had to be measured rather than guessed. Base opinion between two
 * crowns of the same faith across a shared border settles at +19; two crowns of
 * different faiths sit between -15 and -51 and never climb out. So an alliance
 * threshold of 45 — the number this was first written with — produced a realm in
 * which no alliance was ever signed in a hundred and forty turns.
 *
 * The chain it is tuned for now is: **marry first, ally later.** A marriage costs
 * nothing and asks only that they do not actively dislike you, and it is worth
 * +20, which is exactly what lifts a same-faith neighbour over the alliance bar.
 * That is both the EU4 progression and, more importantly, a thing a player can
 * work out by looking at two numbers on a panel.
 */
export const ALLIANCE_OPINION = 20;
export const MARRIAGE_OPINION = -20;

export function acceptsAlliance(c: CampaignState, asked: number, asker: number): boolean {
  if (allianceReason(c, asked, asker) !== '') return false;
  return opinion(c, asked, asker) >= ALLIANCE_OPINION;
}

/**
 * Ask. Resolved on the spot against an AI; parked as an offer against the player.
 *
 * Returns true when the alliance now stands, which is why the AI path and the
 * player path do not return the same thing for the same input — asking the player
 * has not succeeded, it has only been asked.
 */
export function proposeAlliance(c: CampaignState, from: number, to: number): boolean {
  if (allianceReason(c, from, to) !== '') return false;
  if (to === c.playerNation) {
    addOffer(c, {
      kind: OfferKind.Alliance,
      from,
      to,
      war: -1,
      term: -1,
      province: -1,
      gold: 0,
      fromConcedes: false,
      expires: c.turn + OFFER_TURNS,
    });
    logLine(c, `${nationName(c, from)} proposes an alliance.`);
    return false;
  }
  if (!acceptsAlliance(c, to, from)) {
    // A refusal is not free for the asker: they have shown what they wanted.
    addOpinion(c, from, to, -4);
    return false;
  }
  if (!addTreaty(c, TreatyKind.Alliance, from, to)) return false;
  logLine(c, `${nationName(c, from)} and ${nationName(c, to)} swear an alliance.`);
  return true;
}

export function breakAllianceWith(c: CampaignState, breaker: number, other: number): boolean {
  if (!removeTreaty(c, TreatyKind.Alliance, breaker, other)) return false;
  breakAlliancePenalty(c, breaker, other);
  logLine(c, `${nationName(c, breaker)} tears up the alliance with ${nationName(c, other)}.`);
  return true;
}

// ── Royal marriages ────────────────────────────────────────────────────────

export function marriageReason(c: CampaignState, a: number, b: number): string {
  if (a === b || a < 0 || b < 0) return 'There is nobody there.';
  if (!c.nations[a]?.alive || !c.nations[b]?.alive) return 'They no longer hold anything.';
  if (hasMarriage(c, a, b)) return 'The houses are already joined.';
  if (c.nations[a].atWar[b] === 1) return 'You are at war with them.';
  return '';
}

export function acceptsMarriage(c: CampaignState, asked: number, asker: number): boolean {
  if (marriageReason(c, asked, asker) !== '') return false;
  return opinion(c, asked, asker) >= MARRIAGE_OPINION;
}

/**
 * A marriage is cheap, and it is the longest lever in this system.
 *
 * It costs nothing and gives a modest opinion bonus, which is why the AI signs
 * them readily and why a player might sign one with somebody they do not
 * especially like. What it *buys* is two things that do not arrive for decades: a
 * claim on their throne once it has stood twenty turns, and a small chance every
 * turn thereafter that their crown simply falls to you. That is the shape a
 * royal marriage should have — nearly free, nearly pointless, and occasionally
 * the reason a map looks the way it does.
 */
export function proposeMarriage(c: CampaignState, from: number, to: number): boolean {
  if (marriageReason(c, from, to) !== '') return false;
  if (to === c.playerNation) {
    addOffer(c, {
      kind: OfferKind.Marriage,
      from,
      to,
      war: -1,
      term: -1,
      province: -1,
      gold: 0,
      fromConcedes: false,
      expires: c.turn + OFFER_TURNS,
    });
    logLine(c, `${nationName(c, from)} offers a royal marriage.`);
    return false;
  }
  if (!acceptsMarriage(c, to, from)) return false;
  if (!addTreaty(c, TreatyKind.RoyalMarriage, from, to)) return false;
  logLine(c, `${nationName(c, from)} and ${nationName(c, to)} join their houses.`);
  return true;
}

// ── Right of passage ───────────────────────────────────────────────────────

/**
 * Why `granter` could not give `asker` the run of its ground, or the empty
 * string if it could.
 *
 * The list is short on purpose. Passage is meant to be *askable* — the whole
 * point of the feature is that a closed border is a decision somebody made
 * rather than a rule of the world — so the only things that refuse it outright
 * are the ones where the answer is already settled by something else: a war
 * between the two, one realm's ground being the other's already, and a grant
 * that is standing.
 */
export function passageReason(c: CampaignState, granter: number, asker: number): string {
  if (granter === asker || granter < 0 || asker < 0) return 'There is nobody there.';
  if (!c.nations[granter]?.alive || !c.nations[asker]?.alive) return 'They no longer hold anything.';
  if (hasPassage(c, granter, asker)) return 'Their roads are already open to you.';
  if (c.nations[granter].atWar[asker] === 1) return 'You are at war with them. Take it.';
  if (sameRealm(c, granter, asker)) return 'You are of one realm. Nobody asks.';
  return '';
}

/**
 * Whether an AI crown lets a host through, and it is two numbers the panel can
 * print.
 *
 * **They like you enough**, at a bar set above the alliance bar — passage is
 * asked for in wartime by somebody who means to march an army across your
 * country at a third party, which is a larger thing to agree to than a promise
 * about the future.
 *
 * **Or you are too big to refuse.** This is the half that makes the system a
 * decision rather than a wall, and it is the direct answer to what a refusal
 * costs the refuser. A small crown standing between two large ones is going to
 * be marched through; the only question is whether it happens with its leave or
 * after a war it cannot win. So a crown outweighed by `PASSAGE_FEAR_RATIO` says
 * yes to anybody it does not actively hate — and a player who has become the
 * largest power on the board finds the roads opening in front of them, which is
 * both correct and quietly ominous.
 *
 * And one clause that overrides both: **nobody lets an army through to hit their
 * own ally.** That is not caution, it is what an alliance means.
 */
export function acceptsPassage(c: CampaignState, granter: number, asker: number): boolean {
  if (passageReason(c, granter, asker) !== '') return false;
  for (const ally of alliesOf(c, granter)) {
    if (c.nations[asker]?.atWar[ally] === 1) return false;
  }
  const op = opinion(c, granter, asker);
  if (op >= PASSAGE_OPINION) return true;
  if (op < PASSAGE_FEAR_OPINION) return false;
  return coalitionStrength(c, asker) >= coalitionStrength(c, granter) * PASSAGE_FEAR_RATIO;
}

/**
 * Send the herald. Resolved on the spot against an AI; parked as an offer
 * against the player, exactly as an alliance is.
 *
 * Returns true when the road is now open, which is why the two paths do not
 * return the same thing for the same input — asking the player has not
 * succeeded, it has only been asked.
 */
export function requestPassage(c: CampaignState, asker: number, granter: number): boolean {
  if (passageReason(c, granter, asker) !== '') return false;
  if (granter === c.playerNation) {
    if (hasOfferFrom(c, OfferKind.Passage, asker, granter)) return false;
    addOffer(c, {
      kind: OfferKind.Passage,
      from: asker,
      to: granter,
      war: -1,
      term: -1,
      province: -1,
      gold: 0,
      fromConcedes: false,
      expires: c.turn + OFFER_TURNS,
    });
    logLine(c, `${nationName(c, asker)} asks leave to march across your country.`);
    return false;
  }
  if (!acceptsPassage(c, granter, asker)) {
    refusePassage(c, granter, asker);
    return false;
  }
  return grantPassage(c, granter, asker);
}

/**
 * Open the border. Unilateral — a crown may do this without being asked, and a
 * player courting somebody has a reason to.
 */
export function grantPassage(c: CampaignState, granter: number, grantee: number): boolean {
  if (passageReason(c, granter, grantee) !== '') return false;
  if (!addPassage(c, granter, grantee)) return false;
  addOpinion(c, grantee, granter, OP_GRANTED_PASSAGE);
  logLine(c, `${nationName(c, granter)} opens its roads to ${nationName(c, grantee)}.`);
  return true;
}

/**
 * What a refusal costs the crown that refuses, and it is deliberately not a new
 * mechanic.
 *
 * The asker thinks worse of the refuser, and opinion is already what
 * `DECLARE_SPITE` reads when it lowers a crown's bar for declaring war on
 * somebody it dislikes. So saying no to a strong neighbour has made you
 * measurably more likely to be that neighbour's next war, through arithmetic the
 * player can already read off the panel — rather than through a bespoke "they
 * refused me" casus belli, which was considered and cut because every crown you
 * would ever ask is a crown you border, and `Conquest` is already available
 * against everybody you border.
 */
export function refusePassage(c: CampaignState, granter: number, asker: number): void {
  addOpinion(c, asker, granter, OP_REFUSED_PASSAGE);
}

/**
 * Shut the border again, on somebody whose army may well be standing behind it.
 *
 * The most aggressive act in this file short of a declaration, and priced like
 * one: the crown whose hosts are suddenly in the open thinks a great deal worse
 * of you, and the rest of the realm notices, because from the outside a crown
 * that strands an allied army is a crown whose word has a shelf life.
 *
 * It does not move the hosts. What happens to them is `passageTurn`'s business —
 * grace, then hunger — and they may always walk out. That separation is the
 * point: revoking is a decision with a consequence that unfolds, not a button
 * that deletes an army.
 */
export function revokePassage(c: CampaignState, granter: number, grantee: number): boolean {
  if (!removePassage(c, granter, grantee)) return false;
  addOpinion(c, grantee, granter, OP_REVOKED_PASSAGE);
  const stranded = c.armies.some(
    (a) => a.owner === grantee && a.aboard < 0 && c.provinces[a.at]?.owner === granter,
  );
  if (stranded) {
    addOpinionRealm(c, granter, OP_REVOKED_REALM, [granter]);
    logLine(
      c,
      `${nationName(c, granter)} closes its borders to ${nationName(c, grantee)} — ` +
        `and there are hosts of theirs still standing on it.`,
    );
  } else {
    logLine(c, `${nationName(c, granter)} closes its borders to ${nationName(c, grantee)}.`);
  }
  return true;
}

// ── Subjects ───────────────────────────────────────────────────────────────

/**
 * Ask a crown to swear to you without fighting it first.
 *
 * The bar is deliberately near-impossible: they must both like you a great deal
 * and be very much smaller than you. Vassalage is meant to be something you win
 * in a peace; this exists so that the one case where it *should* be possible —
 * a tiny, friendly neighbour with nobody else to turn to — is not artificially
 * closed off.
 */
export const VASSALAGE_OPINION = 85;
export const VASSALAGE_STRENGTH_RATIO = 3;

export function vassalageReason(c: CampaignState, liege: number, subject: number): string {
  if (liege === subject || liege < 0 || subject < 0) return 'There is nobody there.';
  if (!c.nations[liege]?.alive || !c.nations[subject]?.alive) return 'They no longer hold anything.';
  if (liegeOf(c, subject) >= 0) return `${nationName(c, subject)} already answers to somebody.`;
  if (liegeOf(c, liege) >= 0) return 'A sworn crown has no vassals of its own.';
  if (c.nations[liege].atWar[subject] === 1) return 'Ask them after the war, not during it.';
  return '';
}

export function acceptsVassalage(c: CampaignState, subject: number, liege: number): boolean {
  if (vassalageReason(c, liege, subject) !== '') return false;
  if (opinion(c, subject, liege) < VASSALAGE_OPINION) return false;
  return nationStrength(c, liege) >= nationStrength(c, subject) * VASSALAGE_STRENGTH_RATIO;
}

export function demandVassalage(c: CampaignState, liege: number, subject: number): boolean {
  if (vassalageReason(c, liege, subject) !== '') return false;
  if (subject === c.playerNation) {
    addOffer(c, {
      kind: OfferKind.Vassalage,
      from: liege,
      to: subject,
      war: -1,
      term: -1,
      province: -1,
      gold: 0,
      fromConcedes: true,
      expires: c.turn + OFFER_TURNS,
    });
    logLine(c, `${nationName(c, liege)} suggests, politely, that you might swear to them.`);
    return false;
  }
  if (!acceptsVassalage(c, subject, liege)) return false;
  setSubject(c, subject, liege, SubjectKind.Vassal);
  logLine(c, `${nationName(c, subject)} swears to ${nationName(c, liege)} without a blow struck.`);
  return true;
}

/** Why this subject may not be swallowed yet, in words. */
export function annexReason(c: CampaignState, liege: number, subject: number): string {
  const n = c.nations[subject];
  if (!n || !n.alive) return 'There is nobody there.';
  if (n.liege !== liege) return 'They do not answer to you.';
  const need = n.subject === SubjectKind.Union ? UNION_INTEGRATE_TURNS : ANNEX_TURNS;
  const held = c.turn - n.subjectSince;
  if (held < need) return `Not yet — ${need - held} more turns of loyalty.`;
  if (opinion(c, subject, liege) < ANNEX_OPINION) {
    return `They must think better of you first (${Math.round(opinion(c, subject, liege))} of ${ANNEX_OPINION}).`;
  }
  return '';
}

/**
 * Swallow a subject whole: its ground, its hosts and its subjects.
 *
 * The armies come across rather than being disbanded, which is the point of
 * having waited — annexation is the payoff for a long, patient relationship, and
 * a payoff that hands you a dozen provinces and no way to hold them is a trap
 * rather than a reward. The realm's opinion of you drops as if you had taken the
 * lot in a peace, because from the outside that is precisely what happened.
 */
export function annexSubject(c: CampaignState, liege: number, subject: number): boolean {
  if (annexReason(c, liege, subject) !== '') return false;
  const wasUnion = c.nations[subject].subject === SubjectKind.Union;

  let moved = 0;
  for (const p of c.provinces) {
    if (p.owner !== subject) continue;
    p.owner = liege;
    p.claimedBy = subject;
    p.unrest = Math.min(1, p.unrest + 0.25);
    moved++;
  }
  for (const a of c.armies) if (a.owner === subject) a.owner = liege;
  // Its own sworn crowns pass upward rather than being freed. A chain of
  // subjection that quietly dissolves when its middle link is removed is how a
  // player loses half an empire to a button they thought was harmless.
  for (const s of subjectsOf(c, subject)) setSubject(c, s, liege, c.nations[s].subject);

  freeSubject(c, subject);
  dropAllTreaties(c, subject);
  c.nations[subject].alive = false;

  addOpinionRealm(c, liege, OP_TOOK_LAND_REALM, [liege]);
  logLine(
    c,
    wasUnion
      ? `${nationName(c, subject)} is inherited outright. ${moved} provinces come with the crown.`
      : `${nationName(c, liege)} swallows ${nationName(c, subject)} — ${moved} provinces, and no war.`,
  );
  checkElimination(c);
  return true;
}

/** Let one go. It costs nothing and they will remember it for a long time. */
export function releaseSubject(c: CampaignState, liege: number, subject: number): boolean {
  if (c.nations[subject]?.liege !== liege) return false;
  freeSubject(c, subject);
  addOpinion(c, subject, liege, 40);
  logLine(c, `${nationName(c, liege)} releases ${nationName(c, subject)} from its oath.`);
  return true;
}

// ── Answering what is on the table ─────────────────────────────────────────

/**
 * Say yes or no to something that was put to you.
 *
 * One function for all five kinds, because the interesting part is not the
 * branch — it is that refusing is never free, and that a refusal by *silence*
 * costs exactly what a refusal by word does. `expireOffers` calls straight into
 * the same refusal paths.
 */
export function answerOffer(c: CampaignState, offerId: number, accept: boolean): boolean {
  const offer = c.diplo.offers.find((o) => o.id === offerId);
  if (!offer) return false;
  removeOffer(c, offer.id);
  return accept ? acceptOffer(c, offer) : declineOffer(c, offer);
}

function acceptOffer(c: CampaignState, offer: DiploOffer): boolean {
  switch (offer.kind) {
    case OfferKind.CallToArms: {
      const war = warById(c, offer.war);
      if (!war) return false;
      const side = sideOf(war, offer.from);
      if (side === 0) return false;
      joinWar(c, war, offer.to, side);
      addOpinion(c, offer.from, offer.to, 22);
      logLine(c, `${nationName(c, offer.to)} answers ${nationName(c, offer.from)}'s call.`);
      return true;
    }
    case OfferKind.Peace: {
      const war = warById(c, offer.war);
      if (!war) return false;
      // `fromConcedes` says which end of the offer is paying. The crown that is
      // *taking* is the one that signs, whichever end of the table it sits at.
      const taker = offer.fromConcedes ? offer.to : offer.from;
      return makePeace(c, war, taker, offer.term, offer.province);
    }
    case OfferKind.Alliance:
      if (!addTreaty(c, TreatyKind.Alliance, offer.from, offer.to)) return false;
      logLine(c, `${nationName(c, offer.to)} takes ${nationName(c, offer.from)}'s hand.`);
      return true;
    case OfferKind.Marriage:
      if (!addTreaty(c, TreatyKind.RoyalMarriage, offer.from, offer.to)) return false;
      logLine(c, `${nationName(c, offer.to)} and ${nationName(c, offer.from)} join their houses.`);
      return true;
    case OfferKind.Vassalage:
      setSubject(c, offer.to, offer.from, SubjectKind.Vassal);
      logLine(c, `${nationName(c, offer.to)} swears to ${nationName(c, offer.from)}.`);
      return true;
    case OfferKind.Passage:
      // `to` is the one being asked, so `to` is the one whose ground it is.
      return grantPassage(c, offer.to, offer.from);
    default:
      return false;
  }
}

function declineOffer(c: CampaignState, offer: DiploOffer): boolean {
  switch (offer.kind) {
    case OfferKind.CallToArms:
      refuseCall(c, offer.from, offer.to);
      return true;
    case OfferKind.Peace:
      addOpinion(c, offer.from, offer.to, -8);
      return true;
    case OfferKind.Alliance:
      addOpinion(c, offer.from, offer.to, -12);
      logLine(c, `${nationName(c, offer.to)} declines ${nationName(c, offer.from)}'s alliance.`);
      return true;
    case OfferKind.Marriage:
      addOpinion(c, offer.from, offer.to, -6);
      return true;
    case OfferKind.Vassalage:
      addOpinionBoth(c, offer.from, offer.to, -10);
      return true;
    case OfferKind.Passage:
      // Silence and a flat no cost the same, which is the rule the whole offer
      // system keeps — see `expireOffers`. The refuser is `to`.
      refusePassage(c, offer.to, offer.from);
      logLine(
        c,
        `${nationName(c, offer.to)} refuses ${nationName(c, offer.from)} the road.`,
      );
      return true;
    default:
      return false;
  }
}

/**
 * Offers that were never answered.
 *
 * Silence is an answer. A call to arms allowed to lapse breaks the alliance and
 * marks the crown across the whole realm exactly as an outright refusal would,
 * because from the ally standing in the road there is no difference.
 */
export function expireOffers(c: CampaignState): void {
  const lapsed = c.diplo.offers.filter((o) => o.expires <= c.turn);
  for (const offer of lapsed) {
    removeOffer(c, offer.id);
    if (offer.kind === OfferKind.CallToArms) {
      refuseCall(c, offer.from, offer.to);
      addOpinionRealm(c, offer.to, Math.round(OP_ABANDONED_REALM / 3), [offer.to]);
    }
    // A herald left standing at the gate has been refused. Same cost as a flat
    // no, for the same reason a call to arms costs the same either way: from
    // where the asker is standing there is no difference.
    if (offer.kind === OfferKind.Passage) refusePassage(c, offer.to, offer.from);
  }
}

/** Everything waiting on the player, for the panel. */
export function pendingOffers(c: CampaignState): DiploOffer[] {
  return offersFor(c, c.playerNation);
}

// ── One read for the whole panel ───────────────────────────────────────────

/**
 * Everything the world map needs to draw a row per crown, in one call.
 *
 * `src/ui/worldmap.ts` belongs to somebody else this week, so the shape of the
 * panel is described here instead of built: one row per living crown, sorted by
 * how much they matter to you rather than by index, carrying its own reasons for
 * why each button is available or greyed. A UI that has to call nine readers and
 * work out the greying itself will get the greying subtly wrong, and a button
 * that lies is worse than a button that is missing.
 */
export interface CrownView {
  nation: number;
  name: string;
  alive: boolean;
  confession: number;
  confessionName: string;
  /** What they think of you, and what you think of them. Not the same number. */
  theirOpinion: number;
  yourOpinion: number;
  /** Itemised, for the tooltip under the number. */
  terms: Array<{ label: string; value: number }>;
  atWar: boolean;
  allied: boolean;
  married: boolean;
  /**
   * The two halves of passage, and they are separate numbers on purpose.
   *
   * A player who can see that they have opened their roads to a crown that has
   * not opened theirs is looking at the thing this whole system is about. A
   * single "we have an arrangement" boolean would hide exactly the asymmetry
   * that makes the arrangement worth anything.
   */
  theyGrantYou: boolean;
  youGrantThem: boolean;
  /** Empty when you could ask them for passage; otherwise why not. */
  passageBlocked: string;
  /** True when a herald of yours is already standing at their gate. */
  passageAsked: boolean;
  truceTurns: number;
  /** `SubjectKind`, when they answer to you. `None` otherwise. */
  theirSubjection: number;
  /** Who they answer to, or -1. */
  liege: number;
  provinces: number;
  strength: number;
  /** Reasons you could give for a war, best first. Always ends in `None`. */
  casusBelli: number[];
  /** Empty when a war may be declared; otherwise the sentence to put on the button. */
  warBlocked: string;
  allianceBlocked: string;
  marriageBlocked: string;
  /** Empty when this subject may be swallowed; otherwise why not. */
  annexBlocked: string;
}

export function diplomaticView(c: CampaignState, nation: number): CrownView[] {
  const out: CrownView[] = [];
  for (let n = 0; n < c.nations.length; n++) {
    if (n === nation) continue;
    const other = c.nations[n];
    if (!other.alive) continue;
    out.push({
      nation: n,
      name: nationName(c, n),
      alive: other.alive,
      confession: other.confession,
      confessionName: confessionDef(other.confession).name,
      theirOpinion: Math.round(opinion(c, n, nation)),
      yourOpinion: Math.round(opinion(c, nation, n)),
      terms: opinionTerms(c, nation, n),
      atWar: c.nations[nation].atWar[n] === 1,
      allied: isAllied(c, nation, n),
      married: hasMarriage(c, nation, n),
      theyGrantYou: hasPassage(c, n, nation),
      youGrantThem: hasPassage(c, nation, n),
      passageBlocked: passageReason(c, n, nation),
      passageAsked: hasOfferFrom(c, OfferKind.Passage, nation, n),
      truceTurns: truceTurnsLeft(c, nation, n),
      theirSubjection: other.liege === nation ? other.subject : SubjectKind.None,
      liege: other.liege,
      provinces: provincesHeld(c, n),
      strength: Math.round(nationStrength(c, n)),
      casusBelli: casusBelliAgainst(c, nation, n),
      warBlocked: declareWarReason(c, nation, n),
      allianceBlocked: allianceReason(c, nation, n),
      marriageBlocked: marriageReason(c, nation, n),
      annexBlocked: other.liege === nation ? annexReason(c, nation, n) : 'They are not yours.',
    });
  }
  // Wars first, then subjects and allies, then whoever is biggest. A panel of
  // thirty kingdoms is unreadable in index order and perfectly readable in this.
  out.sort(
    (a, b) =>
      Number(b.atWar) - Number(a.atWar) ||
      Number(b.theirSubjection > 0) - Number(a.theirSubjection > 0) ||
      Number(b.allied) - Number(a.allied) ||
      b.provinces - a.provinces ||
      a.nation - b.nation,
  );
  return out;
}

/** One row per war this crown is in, with the terms it could ask for today. */
export interface WarView {
  warId: number;
  enemy: number;
  enemyName: string;
  /** Positive means you are winning. */
  score: number;
  turns: number;
  cb: number;
  goal: number;
  battlesWon: number;
  battlesLost: number;
  provincesTaken: number;
  provincesLost: number;
  /** What you may demand right now, cheapest first. Always contains a white peace. */
  options: PeaceOption[];
}

export function warView(c: CampaignState, nation: number): WarView[] {
  const out: WarView[] = [];
  for (const war of warsOf(c, nation)) {
    const side = sideOf(war, nation);
    const enemy = side === 1 ? war.defender : war.attacker;
    out.push({
      warId: war.id,
      enemy,
      enemyName: nationName(c, enemy),
      score: Math.round(scoreFor(war, nation)),
      turns: c.turn - war.startTurn,
      cb: war.cb,
      goal: war.goal,
      battlesWon: side === 1 ? war.battlesWon : war.battlesLost,
      battlesLost: side === 1 ? war.battlesLost : war.battlesWon,
      provincesTaken: side === 1 ? war.taken : war.lost,
      provincesLost: side === 1 ? war.lost : war.taken,
      options: availableTerms(c, war, nation),
    });
  }
  return out;
}

/**
 * The passage half of the panel, in one call: who your roads are open to, whose
 * are open to you, and every host currently standing where it should not be.
 *
 * Both directions of the trespass list, because both matter to the player and
 * for opposite reasons. A host of *theirs* on *your* ground is a thing you did —
 * you revoked, or you signed a peace — and it is about to start starving in your
 * fields, which is either what you wanted or an accident you should be told
 * about. A host of *yours* on *their* ground is a clock, and the number on it is
 * how many months you have to get out.
 */
export interface PassageView {
  /** Crowns whose ground you may cross. */
  youMayCross: number[];
  /** Crowns that may cross yours. */
  theyMayCross: number[];
  /** Hosts of yours standing where they have no right to be. */
  yoursAbroad: TrespassRow[];
  /** Hosts of somebody else's standing on your ground with no right to. */
  theirsHere: TrespassRow[];
}

export interface TrespassRow {
  armyId: number;
  armyName: string;
  /** The crown whose host it is. */
  owner: number;
  ownerName: string;
  province: number;
  provinceName: string;
  /** The crown whose ground it is standing on. */
  landlord: number;
  landlordName: string;
  /** Months of grace left, 0 once the countryside has turned on them. */
  grace: number;
  /** True once it is starving rather than merely overstaying. */
  starving: boolean;
}

export function passageView(c: CampaignState, nation: number): PassageView {
  const yoursAbroad: TrespassRow[] = [];
  const theirsHere: TrespassRow[] = [];
  for (const a of c.armies) {
    if (!isIntruding(c, a)) continue;
    const landlord = c.provinces[a.at].owner;
    const mine = a.owner === nation;
    const onMine = landlord === nation;
    if (!mine && !onMine) continue;
    const row: TrespassRow = {
      armyId: a.id,
      armyName: a.name,
      owner: a.owner,
      ownerName: nationName(c, a.owner),
      province: a.at,
      provinceName: provinceName(a.at),
      landlord,
      landlordName: nationName(c, landlord),
      grace: isTrespassing(a) ? 0 : graceLeft(a),
      starving: isTrespassing(a),
    };
    if (mine) yoursAbroad.push(row);
    else theirsHere.push(row);
  }
  const byId = (x: TrespassRow, y: TrespassRow): number => x.armyId - y.armyId;
  yoursAbroad.sort(byId);
  theirsHere.sort(byId);
  return {
    youMayCross: passageHeldBy(c, nation),
    theyMayCross: passageGrantedBy(c, nation),
    yoursAbroad,
    theirsHere,
  };
}

/** One line describing what an offer is actually asking. For the panel. */
export function offerText(c: CampaignState, offer: DiploOffer): string {
  const who = nationName(c, offer.from);
  switch (offer.kind) {
    case OfferKind.CallToArms: {
      const war = warById(c, offer.war);
      const foe = war ? nationName(c, sideOf(war, offer.from) === 1 ? war.defender : war.attacker) : 'their enemy';
      return `${who} calls you to arms against ${foe}.`;
    }
    case OfferKind.Peace: {
      if (offer.term === 1 && offer.province >= 0) {
        return offer.fromConcedes
          ? `${who} offers ${provinceName(offer.province)} for peace.`
          : `${who} demands ${provinceName(offer.province)} for peace.`;
      }
      if (offer.term === 2) {
        return offer.fromConcedes
          ? `${who} offers ${offer.gold} gold for peace.`
          : `${who} demands ${offer.gold} gold for peace.`;
      }
      if (offer.term === 0) return `${who} offers a white peace.`;
      return offer.fromConcedes ? `${who} offers terms.` : `${who} demands terms.`;
    }
    case OfferKind.Alliance:
      return `${who} proposes an alliance.`;
    case OfferKind.Marriage:
      return `${who} offers a royal marriage.`;
    case OfferKind.Vassalage:
      return `${who} asks you to swear to them.`;
    case OfferKind.Passage: {
      // Name who they mean to use it on. The answer to "should I let them
      // through" is entirely a question about who is on the other side of you.
      const wars = warsOf(c, offer.from);
      const foe = wars.length > 0 ? nationName(c, sideOf(wars[0], offer.from) === 1 ? wars[0].defender : wars[0].attacker) : '';
      return foe
        ? `${who} asks leave to march across your country at ${foe}.`
        : `${who} asks leave to march across your country.`;
    }
    default:
      return `${who} wants something.`;
  }
}
